import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { RefreshCw, BarChart3, MessageSquare, Users, ShoppingCart, Brain } from 'lucide-react';
import { AnalyticsApi, ChatApi, type PerformanceResult } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// ============================================================
// KB stats fetcher — call /api/admin/kb-stats
// ============================================================
async function fetchKbStats() {
  const base = import.meta.env.VITE_API_BASE || 'https://cher-fb-bot.nayaa.workers.dev';
  const res = await fetch(`${base}/api/admin/kb-stats`, { credentials: 'include' });
  if (!res.ok) throw new Error(`kb-stats ${res.status}`);
  return res.json() as Promise<{
    vectorCount: number;
    dimensions: number;
    messageTotal: number;
    conversationTotal: number;
  }>;
}

const RANGES = [
  { value: 1, label: 'วันนี้' },
  { value: 7, label: '7 วัน' },
  { value: 30, label: '30 วัน' },
  { value: 90, label: '90 วัน' },
];

export default function AnalyticsPage() {
  const [days, setDays] = useState(7);

  const q = useQuery({
    queryKey: ['analytics', days],
    queryFn: () => AnalyticsApi.get(days),
  });

  const perfQ = useQuery({
    queryKey: ['analytics-performance', days],
    queryFn: () => AnalyticsApi.performance(days),
  });

  const refreshMut = useMutation({
    mutationFn: () => ChatApi.refresh(),
    onSuccess: (res) => {
      toast.success(`Refresh queued — ${res.note ?? 'รอประมวลผล'}`);
    },
    onError: (e) => toast.error(`ผิดพลาด: ${e.message}`),
  });

  const totals = q.data?.totals;
  const volumeByDay = aggregateVolume(q.data?.volumePerDay ?? []);

  const kbQ = useQuery({ queryKey: ['kb-stats'], queryFn: fetchKbStats, staleTime: 60_000 });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">ช่วงเวลา:</span>
        {RANGES.map((r) => (
          <Button
            key={r.value}
            variant={days === r.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDays(r.value)}
          >
            {r.label}
          </Button>
        ))}
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          onClick={() => q.refetch()}
        >
          รีเฟรชสถิติ
        </Button>
        <Button
          size="sm"
          className="btn-cta-primary"
          onClick={() => refreshMut.mutate()}
          disabled={refreshMut.isPending}
        >
          <RefreshCw size={14} className={`mr-1 ${refreshMut.isPending ? 'animate-spin' : ''}`} />
          อัพเดท chat data
        </Button>
      </div>

      {/* KB knowledge base card — explains why bot answers what it does */}
      <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3">
        <div className="flex items-start gap-3">
          <Brain size={28} className="text-emerald-700 mt-1 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-sm flex items-center gap-2">
              คลังความรู้บอต (KB)
              <span className="text-[10px] text-muted-foreground font-normal">
                = ฐานข้อมูล Q&amp;A ที่บอตใช้ตอบลูกค้าตามแชทเก่า
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <div>
                <span className="text-muted-foreground text-xs">Q&amp;A ใน KB:</span>{' '}
                <strong className="text-emerald-700">
                  {kbQ.data?.vectorCount?.toLocaleString() ?? '…'}
                </strong>{' '}
                <span className="text-xs text-muted-foreground">คู่</span>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">ข้อความที่เก็บไว้:</span>{' '}
                <strong>{kbQ.data?.messageTotal?.toLocaleString() ?? '…'}</strong>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">บทสนทนา:</span>{' '}
                <strong>{kbQ.data?.conversationTotal?.toLocaleString() ?? '…'}</strong>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
              📌 บอตจะค้นใน KB ก่อน → ถ้าพบคำตอบที่คล้ายกับคำถามลูกค้าเกิน 80% → ตอบจาก KB.
              ถ้าไม่พบ → ตอบจาก Reply Template (ราคา/โปร) หรือ fallback. <br />
              🔁 อัพเดท KB โดยรัน <code className="bg-white px-1 rounded border">node scripts/build-kb.mjs</code> ใน Mac terminal (ดึงแชทล่าสุด → embed → upsert).
              <br />
              ⚠ ถ้าตัวเลข Q&amp;A น้อยกว่าจำนวนข้อความมาก = แชทซ้ำๆ ถูก dedupe ไปแล้ว (ไม่ใช่ปัญหา — ระบบกรองเฉพาะคำถามที่แตกต่าง).
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={<MessageSquare size={16} />}
          label="messages"
          value={totals?.messages ?? 0}
          desc={`${days} วันล่าสุด`}
        />
        <StatCard
          icon={<Users size={16} />}
          label="active customers"
          value={totals?.active_customers ?? 0}
          desc={`คุยใน ${days} วันล่าสุด`}
        />
        <StatCard
          icon={<Users size={16} />}
          label="total customers"
          value={totals?.total_customers ?? 0}
          desc="all-time"
        />
        <StatCard
          icon={<ShoppingCart size={16} />}
          label="orders"
          value={totals?.orders_in_period ?? 0}
          desc={`${days} วันล่าสุด`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title={<><BarChart3 size={14} className="inline mr-1" />Volume per day</>}>
          <VolumeChart data={volumeByDay} />
        </Section>

        <Section title="Customer types">
          <CustomerTypesChart data={q.data?.customerTypes ?? []} />
        </Section>
      </div>

      {perfQ.data && <PerformanceSection data={perfQ.data} />}

      <Section title="Top intents (จาก messages.ai_intent)">
        {q.data && q.data.topIntents.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            ยังไม่มีข้อมูล ai_intent — บอตยังไม่ได้ classify intent (Phase 2)
          </p>
        ) : (
          <div className="space-y-1">
            {q.data?.topIntents.map((i) => (
              <div key={i.ai_intent} className="flex items-center gap-2 text-sm">
                <code className="font-mono text-xs w-40">{i.ai_intent}</code>
                <div className="flex-1 h-3 bg-muted rounded">
                  <div
                    className="h-full bg-amber-400 rounded"
                    style={{
                      width: `${Math.min(100, (i.count / (q.data?.topIntents[0]?.count || 1)) * 100)}%`,
                    }}
                  />
                </div>
                <span className="font-mono w-16 text-right">{i.count}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  desc: string;
}) {
  return (
    <div className="border border-border rounded-lg p-3">
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold font-mono mt-1">
        {value.toLocaleString('th-TH')}
      </div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-border rounded-lg p-3">
      <div className="text-sm font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

function PerformanceSection({ data }: { data: PerformanceResult }) {
  const coverage = data.coverage;
  const kbRate = data.kbHitRate;
  const coveragePct = coverage && coverage.total_in
    ? ((coverage.replied / coverage.total_in) * 100).toFixed(1)
    : '—';
  const botPct = kbRate && kbRate.total
    ? ((kbRate.with_intent / kbRate.total) * 100).toFixed(1)
    : '—';

  return (
    <div className="space-y-4">
      <div className="border-t-2 border-amber-300 pt-3">
        <h2 className="text-lg font-bold">📊 ประสิทธิภาพการตอบลูกค้า</h2>
        <p className="text-xs text-muted-foreground">
          วัด response time / coverage / KB hit rate / admin breakdown
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Reply Coverage</div>
          <div className="text-2xl font-bold font-mono mt-1">{coveragePct}%</div>
          <div className="text-xs text-muted-foreground">
            {coverage?.replied ?? 0}/{coverage?.total_in ?? 0} messages ตอบแล้ว
          </div>
        </div>
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Unanswered</div>
          <div className="text-2xl font-bold font-mono mt-1 text-red-600">
            {coverage?.unanswered ?? 0}
          </div>
          <div className="text-xs text-muted-foreground">messages ที่ยังไม่ตอบ</div>
        </div>
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Bot reply (intent-tagged)</div>
          <div className="text-2xl font-bold font-mono mt-1">{botPct}%</div>
          <div className="text-xs text-muted-foreground">
            {kbRate?.with_intent ?? 0}/{kbRate?.total ?? 0} bot replies tagged
          </div>
        </div>
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Bot avg confidence</div>
          <div className="text-2xl font-bold font-mono mt-1">
            {kbRate?.avg_confidence != null ? kbRate.avg_confidence.toFixed(2) : '—'}
          </div>
          <div className="text-xs text-muted-foreground">cosine similarity (0-1)</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Response Time (in → first reply)">
          {data.responseTime.length === 0 ? (
            <p className="text-xs text-muted-foreground">ยังไม่มีข้อมูล</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">sender</th>
                  <th className="py-1 text-right">replies</th>
                  <th className="py-1 text-right">avg</th>
                  <th className="py-1 text-right">min</th>
                  <th className="py-1 text-right">max</th>
                </tr>
              </thead>
              <tbody>
                {data.responseTime.map((r) => (
                  <tr key={r.out_sender} className="border-t border-border">
                    <td className="py-1 font-mono">{r.out_sender}</td>
                    <td className="py-1 text-right font-mono">{r.replies}</td>
                    <td className="py-1 text-right font-mono">{fmtSec(r.avg_seconds)}</td>
                    <td className="py-1 text-right font-mono text-emerald-600">
                      {fmtSec(r.min_seconds)}
                    </td>
                    <td className="py-1 text-right font-mono text-red-600">
                      {fmtSec(r.max_seconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Replies by sender">
          {data.repliesBySender.length === 0 ? (
            <p className="text-xs text-muted-foreground">ยังไม่มีข้อมูล</p>
          ) : (
            <div className="space-y-1">
              {data.repliesBySender.map((r) => {
                const total = data.repliesBySender.reduce((s, x) => s + x.count, 0);
                const pct = ((r.count / (total || 1)) * 100).toFixed(1);
                return (
                  <div key={r.sender_type} className="flex items-center gap-2 text-sm">
                    <span className="w-20">
                      <code className="text-xs">{r.sender_type}</code>
                    </span>
                    <div className="flex-1 h-3 bg-muted rounded">
                      <div
                        className={`h-full rounded ${
                          r.sender_type === 'bot'
                            ? 'bg-amber-400'
                            : r.sender_type === 'admin'
                              ? 'bg-emerald-400'
                              : 'bg-blue-400'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="font-mono w-16 text-right text-xs">
                      {r.count} ({pct}%)
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>

      {data.topAdmin.length > 0 && (
        <Section title="Top admins (จำนวน reply)">
          <table className="w-full text-xs">
            <tbody>
              {data.topAdmin.map((a) => (
                <tr key={a.sender} className="border-t border-border">
                  <td className="py-1">{a.sender.replace('admin:', '')}</td>
                  <td className="py-1 text-right font-mono">
                    {a.count.toLocaleString('th-TH')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}

function fmtSec(s: number | null): string {
  if (s == null) return '—';
  if (s < 60) return `${s.toFixed(0)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

function aggregateVolume(rows: Array<{ day: string; direction: 'in' | 'out'; count: number }>) {
  const byDay = new Map<string, { day: string; in: number; out: number }>();
  for (const r of rows) {
    if (!byDay.has(r.day)) byDay.set(r.day, { day: r.day, in: 0, out: 0 });
    byDay.get(r.day)![r.direction] = r.count;
  }
  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
}

function VolumeChart({
  data,
}: {
  data: Array<{ day: string; in: number; out: number }>;
}) {
  if (data.length === 0) {
    return <p className="text-xs text-muted-foreground">ยังไม่มีข้อมูล</p>;
  }
  const max = Math.max(...data.flatMap((d) => [d.in, d.out]), 1);
  return (
    <div className="space-y-1">
      <div className="flex gap-2 text-xs text-muted-foreground">
        <span>
          <span className="inline-block w-3 h-3 bg-blue-400 rounded-sm mr-1" /> in (ลูกค้า)
        </span>
        <span>
          <span className="inline-block w-3 h-3 bg-emerald-400 rounded-sm mr-1" /> out (บอต/admin)
        </span>
      </div>
      <table className="w-full text-xs">
        <tbody>
          {data.map((d) => (
            <tr key={d.day}>
              <td className="px-1 py-0.5 text-muted-foreground w-24 font-mono">{d.day}</td>
              <td className="px-1 py-0.5">
                <div className="flex items-center gap-1">
                  <div
                    className="h-3 bg-blue-400 rounded"
                    style={{ width: `${(d.in / max) * 50}%`, minWidth: d.in ? 2 : 0 }}
                    title={`in: ${d.in}`}
                  />
                  <div
                    className="h-3 bg-emerald-400 rounded"
                    style={{ width: `${(d.out / max) * 50}%`, minWidth: d.out ? 2 : 0 }}
                    title={`out: ${d.out}`}
                  />
                  <span className="font-mono text-muted-foreground ml-1">
                    {d.in} / {d.out}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomerTypesChart({
  data,
}: {
  data: Array<{ type: 'new' | 'returning'; count: number }>;
}) {
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) {
    return <p className="text-xs text-muted-foreground">ยังไม่มีข้อมูล</p>;
  }
  return (
    <div className="space-y-2">
      {data.map((d) => {
        const pct = ((d.count / total) * 100).toFixed(1);
        return (
          <div key={d.type} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>
                {d.type === 'new' ? 'ลูกค้าใหม่' : 'ลูกค้าเก่า'}{' '}
                <span className="text-muted-foreground">({pct}%)</span>
              </span>
              <span className="font-mono">{d.count.toLocaleString('th-TH')}</span>
            </div>
            <div className="h-3 bg-muted rounded">
              <div
                className={`h-full rounded ${d.type === 'new' ? 'bg-blue-400' : 'bg-indigo-400'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
